import { Check, X, Info } from "lucide-react";
import { Fragment, useState, useMemo } from "react";
import {
  TooltipRoot,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/primitives/tooltip";
import { FEATURE_ROWS } from "@/config/subscriptionFeatureRows";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { SubscriptionPlan } from "@/server/types/subscription";

const PLAN_COLUMNS: { id: SubscriptionPlan; label: string }[] = [
  { id: "free", label: "Free" },
  { id: "standard", label: "Standard" },
  { id: "pro", label: "Pro" },
  { id: "proplus", label: "Pro+" },
  { id: "premium", label: "Premium" },
];

function CellValue({ value }: { value: string | boolean }) {
  if (value === true) {
    return <Check size={16} strokeWidth={2.5} className="text-green-400" />;
  }
  if (value === false) {
    return (
      <X size={16} strokeWidth={2.5} className="text-muted-foreground/40" />
    );
  }
  return <span className="text-xs text-foreground/80">{value}</span>;
}

interface PlanFeatureTableProps {
  currentPlanId?: SubscriptionPlan;
  highlightCurrentPlan?: boolean;
}

export default function PlanFeatureTable({
  currentPlanId,
  highlightCurrentPlan = false,
}: PlanFeatureTableProps) {
  const { t } = useTranslation();
  const [showOnlyDiff, setShowOnlyDiff] = useState(true);

  const visibleRows = useMemo(() => FEATURE_ROWS.filter((r) => !r.hidden), []);

  const filteredRows = useMemo(
    () =>
      showOnlyDiff
        ? visibleRows.filter(
            (r) => new Set(Object.values(r.values).map(String)).size > 1,
          )
        : visibleRows,
    [showOnlyDiff, visibleRows],
  );

  const categories = useMemo(
    () => [...new Set(filteredRows.map((r) => r.category))],
    [filteredRows],
  );

  const currentColIdx = currentPlanId
    ? PLAN_COLUMNS.findIndex((c) => c.id === currentPlanId)
    : -1;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-[1fr_auto] items-start gap-3">
        <div>
          <h3 className="text-lg font-semibold">
            {t("subscription.compareTiers")}
          </h3>
          <p className="mt-0.5 text-[11.5px] text-muted-foreground">
            {showOnlyDiff
              ? t("subscription.showingDiff")
              : t("subscription.showingAll")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowOnlyDiff((v) => !v)}
          className="shrink-0 rounded-lg border border-border px-3 py-1.5 text-[11.5px] font-medium transition-colors hover:border-secondary/50 hover:bg-muted"
        >
          {showOnlyDiff
            ? t("subscription.showSharedFeatures")
            : t("subscription.hideSharedFeatures")}
        </button>
      </div>

      <TooltipProvider delayDuration={200}>
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full min-w-[540px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-card">
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                  {t("subscription.featureColumn")}
                </th>
                {PLAN_COLUMNS.map((col, i) => (
                  <th
                    key={col.id}
                    className={cn(
                      "p-3 text-center text-sm font-semibold text-foreground",
                      highlightCurrentPlan &&
                        i === currentColIdx &&
                        "text-secondary",
                    )}
                  >
                    {col.label}
                    {highlightCurrentPlan && i === currentColIdx && (
                      <span className="rounded ml-1.5 inline-block bg-secondary px-1 py-0.5 align-middle text-[8.5px] font-bold uppercase tracking-wider text-secondary-foreground">
                        {t("subscription.youBadge")}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {categories.map((category) => {
                const rows = filteredRows.filter(
                  (r) => r.category === category,
                );
                return (
                  <Fragment key={category}>
                    <tr className="border-y border-border/70 bg-card/60">
                      <td
                        colSpan={PLAN_COLUMNS.length + 1}
                        className="px-4 py-2 text-[9.5px] font-semibold uppercase tracking-wider text-secondary"
                      >
                        {category}
                      </td>
                    </tr>
                    {rows.map((row) => (
                      <tr
                        key={`${category}-${row.name}`}
                        className="border-b border-border/50 transition-colors last:border-0"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-medium text-foreground/90">
                              {row.name}
                            </span>
                            {row.info && (
                              <TooltipRoot delayDuration={0}>
                                <TooltipTrigger asChild>
                                  <Info
                                    size={12}
                                    className="cursor-help text-muted-foreground/60"
                                  />
                                </TooltipTrigger>
                                <TooltipContent side="right" avoidCollisions>
                                  {row.info}
                                </TooltipContent>
                              </TooltipRoot>
                            )}
                          </div>
                        </td>
                        {PLAN_COLUMNS.map((col, i) => (
                          <td
                            key={col.id}
                            className={cn(
                              "p-3 text-center",
                              highlightCurrentPlan &&
                                i === currentColIdx &&
                                "bg-secondary/5 text-foreground",
                            )}
                          >
                            <div className="flex justify-center">
                              <CellValue value={row.values[col.id]} />
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </TooltipProvider>
    </div>
  );
}
